import { z } from 'zod';

/**
 * Test impact analysis (Migration 071 / Delivery roadmap Tier 3b.2).
 *
 * Maps changed source files to application routes via the project's
 * route map, intersects that with the per-flow selector/route graph
 * (`flow_graph`), and surfaces a recommended subset for CI.
 */

export const TIA_RECOMMEND_DEFAULT_THRESHOLD = 0.1;

export const FlowGraphRowSchema = z.object({
  flowId: z.string().uuid(),
  selectors: z.array(z.string()),
  routes: z.array(z.string()),
  lastRunId: z.string().uuid().nullable(),
  generatedAt: z.string(),
});
export type FlowGraphRow = z.infer<typeof FlowGraphRowSchema>;

export const ProjectRouteMapEntrySchema = z.object({
  glob: z.string().min(1),
  route: z.string().min(1),
});
export type ProjectRouteMapEntry = z.infer<typeof ProjectRouteMapEntrySchema>;

export const ListProjectRouteMapResponseSchema = z.object({
  entries: z.array(ProjectRouteMapEntrySchema),
});
export type ListProjectRouteMapResponse = z.infer<typeof ListProjectRouteMapResponseSchema>;

export const PutProjectRouteMapRequestSchema = z.object({
  entries: z.array(ProjectRouteMapEntrySchema).max(500),
});
export type PutProjectRouteMapRequest = z.infer<typeof PutProjectRouteMapRequestSchema>;

export const TiaRecommendRequestSchema = z.object({
  /** Files touched by the diff. Repository-relative paths, no globs. */
  files: z.array(z.string().min(1)).max(10_000),
  /** Optional minimum match score; flows below it are dropped. */
  threshold: z.number().min(0).max(1).optional(),
  /** When true, returns the full recommendation list including 0-score flows. */
  includeUnmatched: z.boolean().optional(),
  /**
   * When set, the recommendation is cached against this commit so the
   * release-readiness TIA tile and Quality HQ evidence plan can read it
   * without the caller replaying the diff.
   */
  commitSha: z.string().min(7).max(64).optional(),
  /** Commit / PR message used to link F-#### / BUG-#### on Change Review. */
  message: z.string().max(4_000).optional(),
});
export type TiaRecommendRequest = z.infer<typeof TiaRecommendRequestSchema>;

export const TiaRecommendedFlowSchema = z.object({
  flowId: z.string().uuid(),
  flowName: z.string(),
  /** 0..1 — how strongly the diff intersects this flow's graph. */
  score: z.number().min(0).max(1),
  matchedRoutes: z.array(z.string()),
});
export type TiaRecommendedFlow = z.infer<typeof TiaRecommendedFlowSchema>;

export const TiaRecommendResponseSchema = z.object({
  recommended: z.array(TiaRecommendedFlowSchema),
  /** Flows with no graph yet — fall back to "always run". */
  fallbackFlows: z.array(z.string().uuid()),
  /** Files in the diff that didn't match any glob. */
  unmappedFiles: z.array(z.string()),
  /** Threshold that was applied. */
  threshold: z.number(),
  /**
   * Phase 5E.6 F-02 — when `commitSha` was supplied: whether
   * `applyChangeFromCommit` succeeded. Omitted when no commit was indexed.
   * Distinguishable from an empty recommendation list.
   */
  changeImpactApplied: z.boolean().optional(),
  /** Present only when Change Impact apply failed (TIA recommend still returned). */
  changeImpactApplyError: z
    .object({
      code: z.literal('CHANGE_IMPACT_APPLY_FAILED'),
      message: z.string().min(1).max(500),
    })
    .optional(),
});
export type TiaRecommendResponse = z.infer<typeof TiaRecommendResponseSchema>;

/**
 * Pure glob → route matcher. We support a subset of POSIX globs:
 *
 *   - `**` matches any number of path segments
 *   - `*`  matches a single segment (no `/`)
 *   - `?`  matches any single character (no `/`)
 *   - everything else is literal
 *
 * No external dep — keeps the contracts package zero-dep so it can
 * run in the browser, the CLI, and the API without a build step.
 */
export function globToRegExp(glob: string): RegExp {
  let re = '^';
  for (let i = 0; i < glob.length; i += 1) {
    const ch = glob[i];
    if (ch === '*') {
      if (glob[i + 1] === '*') {
        re += '.*';
        i += 1;
      } else {
        re += '[^/]*';
      }
    } else if (ch === '?') {
      re += '[^/]';
    } else if ('.+^$|()[]{}\\'.includes(ch)) {
      re += '\\' + ch;
    } else {
      re += ch;
    }
  }
  re += '$';
  return new RegExp(re);
}

/**
 * Map a list of changed files to the routes they affect, given the
 * project's route map. Returns the touched routes (deduped) and
 * the files that didn't match any glob.
 */
export function mapFilesToRoutes(
  files: string[],
  routeMap: ProjectRouteMapEntry[],
): { routes: string[]; unmappedFiles: string[] } {
  const compiled = routeMap.map((m) => ({ ...m, re: globToRegExp(m.glob) }));
  const touched = new Set<string>();
  const unmappedFiles: string[] = [];
  for (const file of files) {
    let matched = false;
    for (const m of compiled) {
      if (m.re.test(file)) {
        touched.add(m.route);
        matched = true;
      }
    }
    if (!matched) unmappedFiles.push(file);
  }
  return {
    routes: [...touched].sort(),
    unmappedFiles: [...new Set(unmappedFiles)].sort(),
  };
}

/**
 * A path segment that carries an opaque id rather than a stable route
 * token. Kept deliberately narrow: a false positive here collapses two
 * genuinely different routes into one, which over-selects flows; a
 * false negative only leaves a noisier route in the graph.
 */
function isDynamicSegment(segment: string): boolean {
  if (/^\d+$/.test(segment)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment)) return true;
  if (/^[0-9a-f]{8,}$/i.test(segment)) return true;
  return segment.length >= 12 && /\d/.test(segment) && /^[A-Za-z0-9_-]+$/.test(segment);
}

/**
 * Reduce a URL (or an already-relative path) to its pathname. Returns
 * `null` for anything that isn't URL-shaped — step targets hold either a
 * URL or a CSS/ARIA selector, and the caller needs to tell them apart.
 */
export function pathFromUrlLike(raw: string): string | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return null;
  let pathname: string;
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      pathname = new URL(trimmed).pathname;
    } catch {
      return null;
    }
  } else if (trimmed.startsWith('/')) {
    pathname = trimmed.split(/[?#]/)[0];
  } else {
    return null;
  }
  const collapsed = pathname.replace(/\/{2,}/g, '/');
  if (collapsed.length > 1 && collapsed.endsWith('/')) return collapsed.slice(0, -1);
  return collapsed || '/';
}

/** Replace opaque id segments with `:id` so `/orders/8f21` and `/orders/9c03` agree. */
export function parameterisePath(path: string): string {
  if (path === '/') return path;
  const parameterised = path
    .split('/')
    .map((segment) => (isDynamicSegment(segment) ? ':id' : segment))
    .join('/');
  return parameterised || '/';
}

/**
 * Fold the pathnames a run actually visited onto the routes the operator
 * configured in `project_route_map`, because {@link scoreFlowAgainstRoutes}
 * compares routes by exact string equality — storing `/orders/8f21` would
 * never match a mapped `/orders`.
 *
 * A visited path resolves to its longest mapped prefix (at a segment
 * boundary, so `/order-history` never folds into `/order`). Paths with no
 * mapped prefix are kept in parameterised form: they contribute nothing to
 * TIA scoring, but the story-vs-run coverage gap report reads the same
 * column and a project with no route map would otherwise learn nothing.
 */
export function canonicaliseObservedRoutes(observedPaths: string[], mappedRoutes: string[]): string[] {
  const candidates = [...new Set(mappedRoutes.filter((r) => r.startsWith('/')))].sort((a, b) => b.length - a.length);
  const exact = new Set(candidates);
  // A mapped `/` is the home route, not a prefix of the whole site.
  const prefixes = candidates.filter((route) => route.length > 1);
  const resolved = new Set<string>();
  for (const raw of observedPaths) {
    const path = pathFromUrlLike(raw);
    if (!path) continue;
    if (exact.has(path)) {
      resolved.add(path);
      continue;
    }
    const prefix = prefixes.find((route) => path.startsWith(route.endsWith('/') ? route : `${route}/`));
    resolved.add(prefix ?? parameterisePath(path));
  }
  return [...resolved].sort();
}

/**
 * Score a flow's graph against the touched route set. The score is
 * `(matched routes) / max(1, flow.routes.length)`. Linear, easy to
 * explain to a customer reading the dashboard.
 */
export function scoreFlowAgainstRoutes(
  flowRoutes: string[],
  touchedRoutes: string[],
): { score: number; matchedRoutes: string[] } {
  if (flowRoutes.length === 0) return { score: 0, matchedRoutes: [] };
  const touched = new Set(touchedRoutes);
  const matched: string[] = [];
  for (const r of flowRoutes) {
    if (touched.has(r)) matched.push(r);
  }
  const score = matched.length / flowRoutes.length;
  return { score, matchedRoutes: matched.sort() };
}

/**
 * Walks every flow's graph row and emits the recommendation list
 * sorted by score descending. Flows with no graph are returned
 * separately so callers can fall back to "always run" for them.
 */
export function recommendFlowsForDiff(opts: {
  graphRows: { flowId: string; flowName: string; routes: string[] }[];
  knownFlowIds: string[];
  touchedRoutes: string[];
  threshold?: number;
  includeUnmatched?: boolean;
}): {
  recommended: TiaRecommendedFlow[];
  fallbackFlows: string[];
  threshold: number;
} {
  const threshold = opts.threshold ?? TIA_RECOMMEND_DEFAULT_THRESHOLD;
  const recommended: TiaRecommendedFlow[] = [];
  const indexed = new Set(opts.graphRows.map((r) => r.flowId));

  for (const row of opts.graphRows) {
    const { score, matchedRoutes } = scoreFlowAgainstRoutes(row.routes, opts.touchedRoutes);
    if (score >= threshold || opts.includeUnmatched) {
      recommended.push({
        flowId: row.flowId,
        flowName: row.flowName,
        score,
        matchedRoutes,
      });
    }
  }

  recommended.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.flowName.localeCompare(b.flowName);
  });

  const fallbackFlows = opts.knownFlowIds.filter((id) => !indexed.has(id));

  return { recommended, fallbackFlows, threshold };
}
